import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import DeferredAttachmentSection from "../../components/DeferredAttachmentSection";
import logo from "../../images/logo.png";

const normalizeDateInputValue = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const CanadaPost = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [formData, setFormData] = useState({
    cp_app_start: "",
    cp_comp_received: "",
    cp_certificate_number: "",
    cp_photo_id_pending: false,
    cp_photo_id_yes: false,
    cp_photo_id_no: false,
    cp_photo_id_code: "",
  });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSigned, setIsSigned] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const fetchCanadaPost = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/canadapost/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const data = res.data;
      setFormData({
        cp_app_start: normalizeDateInputValue(data.cp_app_start),
        cp_comp_received: normalizeDateInputValue(data.cp_comp_received),
        cp_certificate_number: data.cp_certificate_number || "",
        cp_photo_id_pending: !!data.cp_photo_id_pending,
        cp_photo_id_yes: !!data.cp_photo_id_yes,
        cp_photo_id_no: !!data.cp_photo_id_no,
        cp_photo_id_code: data.cp_photo_id_code || "",
      });

      setUploadedFiles(data.files || []);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchCanadaPost();
  }, [fetchCanadaPost]);

  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        const TASK_ID = 4; // verify this matches nwt_tasks.t_id for LEEP
        const idKey = String(TASK_ID);
        const nameKey = "canada_post";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "completed");
      } catch (err) {
        console.error("Task check error:", err);
      }
    };

    fetchTaskStatus();
  }, [userId, API_BASE]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "application/vnd.ms-outlook", "application/x-msg", "application/CDFV2", "application/x-ole-storage", "application/octet-stream", ""];

    const invalidFile = selectedFiles.find(
      (selectedFile) => !allowedTypes.includes(selectedFile.type)
    );

    if (invalidFile) {
      alert("Incorrect file format.");
      e.target.value = "";
      return;
    }

    setPendingFiles((prev) => {
      const existingKeys = new Set(
        prev.map((file) => `${file.name}_${file.size}_${file.lastModified}`)
      );
      const next = [...prev];

      selectedFiles.forEach((file) => {
        const key = `${file.name}_${file.size}_${file.lastModified}`;
        if (!existingKeys.has(key)) {
          existingKeys.add(key);
          next.push(file);
        }
      });

      return next;
    });

    e.target.value = "";
  };

  const removePendingFile = (fileToRemove) => {
    setPendingFiles((prev) =>
      prev.filter(
        (file) =>
          !(
            file.name === fileToRemove.name &&
            file.size === fileToRemove.size &&
            file.lastModified === fileToRemove.lastModified
          )
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => data.append(key, value));
    data.append("userId", userId);
    pendingFiles.forEach((file) => data.append("files", file));

    try {
      setIsSaving(true);
      await axios.post(
        `${API_BASE}/api/canadapost/upload?userId=${userId}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      alert("Saved successfully!");
      setPendingFiles([]);
      fetchCanadaPost();
    } catch (error) {
      console.error("Upload error:", error);
      alert(error.response?.data?.message || "Upload failed. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (file) => {
    const confirmed = window.confirm(`Delete uploaded file "${file.name}"?`);
    if (!confirmed) return;

    try {
      await axios.delete(`${API_BASE}/api/canadapost/file`, {
        data: { filePath: file.path, userId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      alert("File deleted");
      fetchCanadaPost();
    } catch (error) {
      console.error("Delete error:", error);
      alert("File deletion failed.");
    }
  };

  const handleCancel = () => {
    navigate(isAdmin ? `/userhome/${paramId}` : "/userhome");
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Canada Post Clearance
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4 print-area">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Canada Post Clearance</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSubmit} className="mt-3">
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Application Start:</label>
              <input
                type="date"
                className="form-control"
                name="cp_app_start"
                value={formData.cp_app_start}
                onChange={handleInputChange}
                disabled={isSigned && !isAdmin}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Company Received:</label>
              <input
                type="date"
                className="form-control"
                name="cp_comp_received"
                value={formData.cp_comp_received}
                onChange={handleInputChange}
                disabled={isSigned && !isAdmin}
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Certificate Number:</label>
            <input
              type="text"
              className="form-control"
              name="cp_certificate_number"
              value={formData.cp_certificate_number}
              onChange={handleInputChange}
              disabled={isSigned && !isAdmin}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Photo ID Code:</label>
            <input
              type="text"
              className="form-control"
              name="cp_photo_id_code"
              value={formData.cp_photo_id_code}
              onChange={handleInputChange}
              disabled={isSigned && !isAdmin}
            />
          </div>

          <div className="row mb-3">
            {["pending", "yes", "no"].map((key, i) => (
              <div className="col-md-4" key={i}>
                <label className="form-label">{`Photo ID - ${
                  key.charAt(0).toUpperCase() + key.slice(1)
                }`}</label>
                <input
                  type="checkbox"
                  className="form-check-input ms-2"
                  name={`cp_photo_id_${key}`}
                  checked={formData[`cp_photo_id_${key}`]}
                  onChange={handleInputChange}
                  disabled={isSigned && !isAdmin}
                />
              </div>
            ))}
          </div>

          <DeferredAttachmentSection
            label="Select File *"
            disabled={(!isAdmin && isSigned) || isSaving}
            pendingFiles={pendingFiles}
            uploadedFiles={uploadedFiles}
            onSelectFiles={handleFileChange}
            onRemovePendingFile={removePendingFile}
            onDeleteUploadedFile={handleDelete}
            getUploadedFileKey={(file) => file.path}
            getUploadedFileName={(file) => file.name}
            getUploadedFileUrl={(file) => `${API_BASE}/${file.path}`}
          />

          <div className="d-flex justify-content-end mt-4 pt-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={(isSigned && !isAdmin) || isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>

        <hr className="my-5" />
        {userId && (
          <>
            <SignatureManager
              taskName="canada_post"
              taskId={4}
              userId={userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="canada_post"
              taskId={4}
              userId={userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default CanadaPost;

